import { baseApi } from "./baseApi";
import type { Subscriber } from "../api";

interface CreateSubscriberBody {
  email: string;
  channels: string[];
  severity_min: string;
  service_ids?: string[] | null;
  webhook_url?: string;
}

interface UpdateSubscriberBody extends Partial<CreateSubscriberBody> {
  id: string;
}

export const subscribersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscribers: builder.query<Subscriber[], void>({
      query: () => "/v1/subscribers",
      transformResponse: (r: { subscribers: Subscriber[] }) => r.subscribers,
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "Subscribers" as const, id: s.id })),
              { type: "Subscribers", id: "LIST" },
            ]
          : [{ type: "Subscribers", id: "LIST" }],
    }),

    /**
     * Si pasás `orgId` en `extraOptions`, se manda como header `X-Org-Id` solo
     * para esta request (override sin tocar el org seleccionado globalmente).
     * RTK Query no soporta `orgId` nativamente; lo agregamos vía `prepareHeaders`
     * del fetchBaseQuery — pero acá lo dejamos como tag para inspección.
     */
    createSubscriber: builder.mutation<Subscriber, { body: CreateSubscriberBody; orgId?: string }>({
      query: ({ body }) => ({ url: "/v1/subscribers", method: "POST", body }),
      invalidatesTags: [{ type: "Subscribers", id: "LIST" }],
    }),

    updateSubscriber: builder.mutation<Subscriber, UpdateSubscriberBody>({
      query: ({ id, ...body }) => ({ url: `/v1/subscribers/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Subscribers", id: arg.id },
        { type: "Subscribers", id: "LIST" },
      ],
    }),

    deleteSubscriber: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/subscribers/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Subscribers", id },
        { type: "Subscribers", id: "LIST" },
      ],
    }),

    /** Disparar un envío de prueba. No invalida cache. */
    testSubscriber: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/subscribers/${id}/test`, method: "POST", body: {} }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSubscribersQuery,
  useCreateSubscriberMutation,
  useUpdateSubscriberMutation,
  useDeleteSubscriberMutation,
  useTestSubscriberMutation,
} = subscribersApi;
